import { useEffect, useState } from "react";
import { Button, Card, Col, Row, Typography } from "antd";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import K from "utilities/constants";
import {
  breakdownDefaultResponsiveSettings,
  breakdownSliderSettings,
  statisticsSliderSettings,
} from "config/sliderConfig";
import { useHistory, useParams } from "react-router-dom";
import {
  checkStatusExist,
  convertIntoSnakeCase,
  removeSpacesAndPascalSentenceCase,
} from "utilities/generalUtility";
import { isNil } from "lodash";

const { Title, Paragraph } = Typography;

export default function JobStatistics({ styles, jobData }) {
  const { statistics, status, subStatus, jobDetails } = jobData;

  const {
    clientId,
    jobId,
    statusesId,
    statusName,
    subStatusesId,
    subStatusName,
  } = useParams();
  const history = useHistory();

  const [pageState, setPageState] = useState({
    statusId: null,
    selectedStatusName: "",
    subStatusId: null,
    selectedSubStatusName: "",
  });

  const [breakdownResponsive, setBreakdownResponsive] = useState(
    breakdownDefaultResponsiveSettings,
  );

  const selectStatus = (
    statusName = "",
    id,
    subStatusName = "",
    subStatusId = null,
  ) => {
    let path = `/clients/${clientId}/job/${jobId}`;
    if (id) {
      path += `/${id}/${convertIntoSnakeCase(statusName)}`;
    }
    if (subStatusId) {
      path += `/${subStatusId}/${convertIntoSnakeCase(subStatusName)}`;
    }

    setPageState((prev) => {
      return {
        statusId: id,
        selectedStatusName: id
          ? removeSpacesAndPascalSentenceCase(statusName)
          : "",
        selectedSubStatusName: subStatusId ? subStatusName : "",
        subStatusId: subStatusId || prev.subStatusId,
      };
    });

    if (id !== statusesId && subStatusId !== subStatusesId) {
      history.replace(path);
    }
  };
  const selectSubStatus = (subStatusName = "", id) => {
    const path = `/clients/${clientId}/job/${jobId}/${
      pageState.statusId
    }/${convertIntoSnakeCase(
      pageState.selectedStatusName,
    )}/${id}/${convertIntoSnakeCase(subStatusName)}`;

    history.replace(path);

    setPageState((prev) => {
      return {
        ...prev,
        selectedSubStatusName: subStatusName,
        subStatusId: id,
      };
    });
  };

  const getTTMDisplayValue = (value) => {
    return isNil(value) ? K.NullPlaceholder : value.toFixed(2);
  };

  const geTotalOpeningsDisplayValue = () => {
    if (jobDetails?.isCancel) {
      return "Cancelled";
    }

    return jobDetails?.vacancies?.totalOpenings ?? 0;
  };

  useEffect(() => {
    if (statusesId) {
      if (subStatusesId && checkStatusExist(statusName, subStatusName)) {
        selectStatus(statusName, statusesId, subStatusName, subStatusesId);
      } else {
        checkStatusExist(statusName, subStatusName) &&
          selectStatus(statusName, statusesId);
      }
    } else {
      selectStatus("", null, "", null);
    }
  }, [statusesId, subStatusesId]);

  useEffect(() => {
    if (Object.keys(subStatus)?.length > 0) {
      if (pageState.selectedStatusName) {
        if (subStatus[pageState.selectedStatusName]?.length < 3) {
          const updated = breakdownDefaultResponsiveSettings.map((item) => {
            if (item.breakpoint === 380) {
              return {
                breakpoint: item.breakpoint,
                settings: {
                  ...item.settings,
                  slidesToShow: 1,
                  slidesToScroll: 1,
                },
              };
            }
            return {
              breakpoint: item.breakpoint,
              settings: {
                ...item.settings,
                slidesToShow: subStatus[pageState.selectedStatusName]?.length,
                slidesToScroll: subStatus[pageState.selectedStatusName]?.length,
              },
            };
          });
          setBreakdownResponsive(updated);
        } else {
          const updated = breakdownDefaultResponsiveSettings.map((item) => {
            if (item.breakpoint === 1318) {
              return {
                breakpoint: item.breakpoint,
                settings: {
                  ...item.settings,
                  slidesToShow:
                    subStatus[pageState.selectedStatusName]?.length < 7
                      ? subStatus[pageState.selectedStatusName]?.length % 7
                      : 7,
                  slidesToScroll:
                    subStatus[pageState.selectedStatusName]?.length < 7
                      ? subStatus[pageState.selectedStatusName]?.length % 7
                      : 7,
                },
              };
            }
            if (item.breakpoint === 576) {
              return {
                breakpoint: item.breakpoint,
                settings: {
                  ...item.settings,
                  slidesToShow: 2,
                  slidesToScroll: 2,
                },
              };
            }
            if (item.breakpoint === 1200) {
              return {
                breakpoint: item.breakpoint,
                settings: {
                  ...item.settings,
                  slidesToShow: 6,
                  slidesToScroll: 3,
                },
              };
            }
            if (item.breakpoint === 1050) {
              return {
                breakpoint: item.breakpoint,
                settings: {
                  ...item.settings,
                  slidesToShow: 4,
                  slidesToScroll: 3,
                },
              };
            }
            if (item.breakpoint === 670) {
              return {
                breakpoint: item.breakpoint,
                settings: {
                  ...item.settings,
                  slidesToShow: 3,
                  slidesToScroll: 3,
                },
              };
            }
            return {
              breakpoint: item.breakpoint,
              settings: {
                ...item.settings,
                slidesToShow: 1,
                slidesToScroll: 1,
              },
            };
          });
          setBreakdownResponsive(updated);
        }
      }
    }
  }, [pageState.selectedStatusName, pageState.selectedSubStatusName]);

  return (
    <>
      <Card
        className={`statistics-card statistics-wrap ${styles.statisticsCard}`}
        title="Statistics"
      >
        <Slider
          {...statisticsSliderSettings}
          className={`statistics-wrap ${styles.statisticsWrap}`}
        >
          <div className={`${styles.statisticsData} statistic-data`}>
            <Title level={5} className={styles.statisticsTitle}>
              Total Openings
            </Title>
            <span className={`${styles.statisticsCount} statistics-count`}>
              {geTotalOpeningsDisplayValue()}
            </span>
          </div>
          <div className={`${styles.statisticsData} statistic-data`}>
            <Title level={5} className={styles.statisticsTitle}>
              Current Openings
            </Title>
            <span className={`${styles.statisticsCount} statistics-count`}>
              {jobDetails?.vacancies?.currentOpenings ?? 0}
            </span>
          </div>
          <div className={`${styles.statisticsData} statistic-data`}>
            <Title level={5} className={styles.statisticsTitle}>
              Canceled
            </Title>
            <span className={`${styles.statisticsCount} statistics-count`}>
              {jobDetails?.vacancies?.cancel ?? 0}
            </span>
          </div>
          <div className={`${styles.statisticsData} statistic-data`}>
            <Title level={5} className={styles.statisticsTitle}>
              Filled
            </Title>
            <span className={`${styles.statisticsCount} statistics-count`}>
              {statistics?.filled ?? 0}
            </span>
          </div>
          <div className={`${styles.statisticsData} statistic-data`}>
            <Title level={5} className={styles.statisticsTitle}>
              Time to Submit
            </Title>
            <span className={`${styles.statisticsCount} statistics-count`}>
              {getTTMDisplayValue(statistics?.timeToSubmit)}
            </span>
          </div>
          <div className={`${styles.statisticsData} statistic-data`}>
            <Title level={5} className={styles.statisticsTitle}>
              Time to Offer
            </Title>
            <span className={`${styles.statisticsCount} statistics-count`}>
              {getTTMDisplayValue(statistics?.timeToOffer)}
            </span>
          </div>
          <div className={`${styles.statisticsData} statistic-data`}>
            <Title level={5} className={styles.statisticsTitle}>
              Time to Accept
            </Title>
            <span className={`${styles.statisticsCount} statistics-count`}>
              {getTTMDisplayValue(statistics?.timeToAccept)}
            </span>
          </div>
          <div className={`${styles.statisticsData} statistic-data`}>
            <Title level={5} className={styles.statisticsTitle}>
              Offer to Hire
            </Title>
            <span className={`${styles.statisticsCount} statistics-count`}>
              {getTTMDisplayValue(statistics?.offerToHire)}
            </span>
          </div>
          <div className={`${styles.statisticsData} statistic-data`}>
            <Title level={5} className={styles.statisticsTitle}>
              Time to Start
            </Title>
            <span className={`${styles.statisticsCount} statistics-count`}>
              {getTTMDisplayValue(statistics?.timeToStart)}
            </span>
          </div>
        </Slider>
      </Card>
      <Card
        bordered={false}
        className={
          `${styles.statusCard} status-card-wrap ` +
          `${!pageState.selectedStatusName ? " mb-4" : ""}`
        }
      >
        <div className={styles.statusCardHeader}>
          <Title level={5}>Status</Title>
          <h5 className="total-number">
            &nbsp;Total{" "}
            {status?.reduce((prev, curr) => {
              return prev + curr.total;
            }, 0)}
          </h5>
        </div>
        <Row
          gutter={[12, 12]}
          // style={{ height: viewMore ? "auto" : "68px", overflow: "hidden" }}
        >
          {status?.map(({ name, total, id }) => (
            <Col
              key={name}
              xs={24}
              sm={12}
              md={8}
              lg={4}
              className={
                convertIntoSnakeCase(pageState.selectedStatusName) ===
                convertIntoSnakeCase(name)
                  ? "box-active"
                  : ""
              }
              onClick={() => selectStatus(convertIntoSnakeCase(name), id)}
            >
              <div
                className={`${styles.statusCardInnerBoxes} status-inner-boxes`}
              >
                <Title level={5}>
                  {total}
                  {convertIntoSnakeCase(pageState.selectedStatusName) ===
                  convertIntoSnakeCase(name) ? (
                    <i className="icon-Vector" />
                  ) : (
                    <i className="icon-dropdown-arrow" />
                  )}
                </Title>
                <Paragraph
                  className={`${styles.jobsDescription} hover-underlined`}
                >
                  {name}
                </Paragraph>
              </div>
            </Col>
          ))}
        </Row>
        {pageState.selectedStatusName && (
          <Card
            title="Status Count Breakdown"
            extra={
              <>
                <Button
                  type="link"
                  className={styles.cardCloseIcon}
                  icon={<i className="icon-closeable" />}
                  onClick={() => {
                    history.replace(`/clients/${clientId}/job/${jobId}`);
                    // getCandidateByJobId();
                  }}
                />
                {/* <Title level={5}>Declined: 03</Title> */}
              </>
            }
            className={`status-count-card count-breakdown-card ${styles.statusCountCard}`}
          >
            <Slider
              className={`status-count-slider ${styles.statusCountSlider}`}
              slidesToShow={
                subStatus[pageState.selectedStatusName]?.length < 7
                  ? subStatus[pageState.selectedStatusName].length % 7
                  : 7
              }
              {...breakdownSliderSettings}
              responsive={breakdownResponsive}
            >
              {subStatus[pageState.selectedStatusName]?.map(
                ({ id, name, candidatesToSubStatusCount }) => (
                  <div
                    key={name}
                    className={
                      convertIntoSnakeCase(pageState.selectedSubStatusName) ===
                      convertIntoSnakeCase(name)
                        ? "count-breakdown-open active"
                        : "count-breakdown-open"
                    }
                    // className="count-breakdown-open active"
                    onClick={() =>
                      selectSubStatus(convertIntoSnakeCase(name), id)
                    }
                  >
                    <Title level={5}>{candidatesToSubStatusCount}</Title>
                    <Paragraph
                      className={`${styles.jobsDescription} hover-underlined`}
                    >
                      {name}
                    </Paragraph>
                  </div>
                ),
              )}
            </Slider>
          </Card>
        )}
      </Card>
    </>
  );
}
